import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import {
  ChatbotRTKService,
  useCreateAssistantBotMutation,
  useCreateKbAssistantBotMutation,
  useLazyGetAllScenariosQuery,
  useLazyGetAssistantsQuery,
} from '@/app/services/chatbot-rtk.services';
import { PackageRTKService, useCreatePackageMutation, useLazyGetPackagesByNameQuery, useMarketplacePaymentMutation } from '@/app/services/package-rtk.service';
import { useCreateNewScenarioMutation } from '@/app/services/scenario-rtk.service';
import { AlertsType } from '@/constants/alerts.constant';
import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';
import { AIKittyType } from '@/features/bot/types';
import { selectClient } from '@/features/dashboard.slice';
import { setCurrentScenario, setCurrentScenarioTitle } from '@/features/scenario.slice';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import { getBotWizardConfig } from '../botWizardConfigs';
import {
  resetCreateFlow,
  selectCreateModalState,
  selectCurrentDataState,
  selectCurrentStepState,
  selectCurrentTypeState,
  setSection,
  setStep,
  setType,
  toggeleCreateModal,
  updateData,
} from '../create.slice';

export function useWizardController() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const reduxData = useAppSelector(selectCurrentDataState);
  const showModal = useAppSelector(selectCreateModalState);
  const currentType = useAppSelector(selectCurrentTypeState);
  const currentStepFromRedux = useAppSelector(selectCurrentStepState);

  const client = useAppSelector(selectClient);
  const botConfigs = client?.botConfigs;

  const [getCloneInstructions, { isLoading: isGettingInstructions }] = useLazyGetAssistantsQuery();
  const [getAllBots, { isLoading: isGettingLimit }] = useLazyGetAllScenariosQuery();

  const [checkPackage, { isLoading: isCheckingPackage }] = useLazyGetPackagesByNameQuery();
  const [createMcBot, { isLoading }] = useCreateNewScenarioMutation();

  const [createAiKittyBot, { isLoading: isCreatingQuickBot }] = useCreateAssistantBotMutation();
  const [createKbAiKittyBot, { isLoading: isCreatingOtherBots }] = useCreateKbAssistantBotMutation();

  const [createPackage, { isLoading: isCreatingPackage }] = useCreatePackageMutation();

  const [paymentPackage, { isLoading: isPaymentLoading }] = useMarketplacePaymentMutation();

  const [stepIndex, setStepIndex] = useState(currentStepFromRedux);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setStepIndex(currentStepFromRedux);
  }, [currentStepFromRedux]);

  const goToStep = useCallback(
    (step: number, section?: string) => {
      setStepIndex(step);
      dispatch(setStep(step));
      if (section) {
        dispatch(setSection(section));
      }
    },
    [dispatch]
  );

  const steps = useMemo(() => {
    return getBotWizardConfig(currentType, t, reduxData, goToStep);
  }, [currentType, t, reduxData, goToStep]);

  const currentStep = steps[stepIndex] || {};
  const isLastStep = stepIndex > 0 && stepIndex === steps.length - 1;
  const isFirstStep = stepIndex === 0;

  useEffect(() => {
    if (stepIndex >= steps.length) {
      setStepIndex(steps.length - 1);
    }
  }, [steps, stepIndex]);

  const resolvedSchema = useMemo(() => {
    if (typeof currentStep?.schema === 'function') {
      return currentStep.schema(reduxData, t);
    }
    return currentStep?.schema || Yup.object();
  }, [currentStep?.schema, reduxData, t]);

  const methods = useForm({
    resolver: yupResolver(resolvedSchema),
    defaultValues: reduxData || {},
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (showModal) {
      // Deep clone to avoid "object is not extensible" errors from frozen Redux state
      const clonedData = reduxData ? JSON.parse(JSON.stringify(reduxData)) : {};
      methods.reset(clonedData);
    } else {
      setStepIndex(0);
    }
  }, [showModal, reduxData]);

  // Update Redux on every successful step submit
  const next = methods.handleSubmit(async stepData => {
    if (stepData.plug_and_play?.address && !stepData.plug_and_play.address.startsWith('https://')) {
      stepData.plug_and_play.address = `https://${stepData.plug_and_play.address}`;
    }

    if (stepData.bot_code === BotTypeEnum.HUMAN_CLONE && stepIndex === 0) {
      if (!stepData.required_instructions) {
        const data = await getCloneInstructions({ language: i18n.language });
        const res = data?.data?.find(a => a.code === 'self_clone');
        stepData.required_instructions = res?.required_instructions || [];
        stepData.assistant = 'self_clone';
      }
    }

    const canGoNext = await handleCanGoNext(currentType === BotTypeEnum.MARKETPLACE ? currentType : stepData.bot_code, stepData?.name);
    if (!canGoNext) return;

    if (stepIndex === 0 && stepData.bot_code && stepData.bot_code !== BotTypeEnum.HUMAN_CLONE) {
      dispatch(setType(stepData.bot_code));
    }

    // Merge latest step data with current reduxData
    const mergedData = { ...reduxData, ...stepData };

    // Update Redux
    dispatch(updateData(stepData));

    // Recalculate steps based on latest merged data
    const updatedSteps = getBotWizardConfig(currentType, t, mergedData, goToStep);
    const lastStepIndex = updatedSteps.length - 1;

    if (stepIndex == 0 || stepIndex < lastStepIndex) {
      goToNextStep();
    } else {
      // last step
      if (mergedData.has_package) {
        PaymentPackageTemplate(mergedData);
      } else {
        const prepared = prepareStepData(mergedData);
        handleFinalSubmit(prepared);
      }
    }
  });

  function prepareStepData(stepData: any) {
    const result = { ...reduxData, ...stepData };

    // Normalize additional_instructions
    if (result.has_package) {
      // Package already exists, no additional processing needed
    } else if (currentType === BotTypeEnum.MARKETPLACE) {
      result.configuration = {
        bots: result.configuration.bots.map(({ name, ...rest }: { name: string }) => rest),
      };
      result.price = Number(result.price);
      result.version = Number(result.version);
      if (result.data.linkedin_link === '') {
        const { linkedin_link, ...rest } = result.data;
        result.data = rest;
      }
      if (result.data.youtube_link === '') {
        const { youtube_link, ...rest } = result.data;
        result.data = rest;
      }
    } else {
      result.additional_instructions = stepData.additional_instructions
        ? stepData.additional_instructions.split('\n')?.filter((x: string) => x.trim() !== '')
        : [];
      // Fallbacks
      result.configuration = result.bot_code === BotTypeEnum.CALLBOT ? 'FAST' : result.configuration || 'FAST_SMART';
      result.assistant = result.assistant || 'free';
      result.instructions = result.instructions || {};
      if (result.bot_code !== BotTypeEnum.HUMAN_CLONE) {
        delete result.cloneExtra;
      }
      delete result.required_instructions;

      // Clean isVerified and sitemap if exists
      if (result.bot_code === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY) {
        const { isVerified, sitemap, ...rest } = result.plug_and_play;
        result.plug_and_play = rest;
      }
    }

    return result;
  }

  function goToNextStep() {
    const nextIndex = stepIndex + 1;
    setStepIndex(nextIndex);
    dispatch(setStep(nextIndex));
  }

  function handleFinalSubmit(result: any) {
    const type = result.bot_code || currentType;

    switch (type) {
      case BotTypeEnum.TEXT_SCENARIO:
      case BotTypeEnum.VIDEO_SCENARIO:
        createMcBot({
          title: result.title,
          bot_code: result.bot_code,
          template_id: JSON.parse(result.template_id),
        })
          .unwrap()
          .then(values => {
            dispatch(setCurrentScenario(values.id));
            dispatch(setCurrentScenarioTitle(values?.config?.title));
            finishFlow();
            navigate(`chatbots/editor?type=${result.bot_code === BotTypeEnum.TEXT_SCENARIO ? 'text-scenario' : 'video-scenario'}`);
          })
          .catch(handleError);

        break;

      case BotTypeEnum.AI_READY_TO_USE:
        createAiKittyBot(result as AIKittyType)
          .unwrap()
          .then(() => {
            toast.success(t(AlertsType.ISKBKO008));
            finishFlow();
            navigate(`chatbots`);
          })
          .catch(handleError);
        break;
      case BotTypeEnum.AI_WITH_KNOWLEDGE_BASE:
      case BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY:
      case BotTypeEnum.HUMAN_CLONE:
      case BotTypeEnum.CALLBOT:
        createKbAiKittyBot(result as AIKittyType)
          .unwrap()
          .then(() => {
            toast.success(t(AlertsType.ISKBKO008));
            finishFlow();
          })
          .catch(handleError);
        break;

      case BotTypeEnum.MARKETPLACE:
        createPackage(result)
          .unwrap()
          .then(() => {
            toast.success('Package created successfully');
            finishFlow();
          })
          .catch(handleError);
    }
  }

  async function handleCanGoNext(bot_code: string, package_name: string) {
    let returnValue = false;
    if (bot_code === BotTypeEnum.MARKETPLACE && stepIndex == 0) {
      await checkPackage({
        limit: '10',
        page: '1',
        query: package_name,
      })
        .unwrap()
        .then(value => {
          if (value?.packages.length > 0) {
            toast.error(t('marketplace/preventExistingPackageName'));
          } else {
            returnValue = true;
          }
        });
    } else {
      await getAllBots({
        botType: bot_code == BotTypeEnum.HUMAN_CLONE ? 'link' : 'widget',
      })
        .unwrap()
        .then(value => {
          const targetBotCodeLimit = botConfigs?.find((bot: any) => bot.botName === bot_code)?.config.limit;

          const totalOfTargetBotInList = value.filter((item: any) => item.type === bot_code);

          if (targetBotCodeLimit && totalOfTargetBotInList.length > 0 && totalOfTargetBotInList.length >= targetBotCodeLimit) {
            toast.warn(t('General/key2'));
            returnValue = false;
          } else returnValue = true;
        });
    }
    return returnValue;
  }
  function finishFlow() {
    methods.reset();
    dispatch(toggeleCreateModal(false));
    dispatch(resetCreateFlow());
    dispatch(ChatbotRTKService.util.invalidateTags(['Package', 'GetAllScenarios']));
    dispatch(PackageRTKService.util.invalidateTags(['GetPackagesVersionList']));
    setLoading(false);
  }

  function handleError(err: any) {
    if (err?.data?.code === 'ISSYKO004') {
      let messages = '';
      err?.data?.element?.forEach((item: any) => {
        messages += `${Object.values(item?.messages)} \n`;
      });
      toast.error(messages);
    } else if (err?.data?.code == 'ISSYKO007') {
      toast.error(t(AlertsType.ISSYKO007));
    } else if (err?.data?.code == 'ISSYKO008') {
      toast.error(t(AlertsType.ISSYKO008));
    } else {
      toast.error(err?.data?.message || err.message || 'An error occurred');
    }
  }

  const back = () => {
    methods.clearErrors();
    if (!isFirstStep) {
      const prevIndex = stepIndex - 1;
      setStepIndex(prevIndex);
      dispatch(setStep(prevIndex));
      if (reduxData?.has_package) {
        steps.splice(-1);
      }
    }
  };

  const PaymentPackageTemplate = (stepData: any) => {
    paymentPackage({
      redirectUrl: `${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel/verify-payment?payment_package_id=${stepData.package.package_id}`,
      amount: stepData.package.price,
      currency: 'USD',
      productId: stepData.package.package_id,
    })
      .unwrap()
      .then(res => {
        setLoading(true);
        const newWin = window.open(res, '_self');
        if (newWin) {
          const timer = setInterval(() => {
            if (newWin.closed) {
              clearInterval(timer);
              finishFlow();
            }
          }, 500);
        }
      })
      .catch(err => {
        toast.error(err.data.message || err.message);
        finishFlow();
        return;
      });
  };

  return {
    methods,
    Component: currentStep?.Component || (() => null),
    stepIndex,
    isFirstStep,
    isLastStep,
    next,
    back,
    props: currentStep?.props,
    isLoading:
      isLoading ||
      isCreatingQuickBot ||
      isCreatingOtherBots ||
      isGettingInstructions ||
      isGettingLimit ||
      isCreatingPackage ||
      isPaymentLoading ||
      isCheckingPackage ||
      loading,
    goToStep,
  };
}
